import type { SupabaseClient } from '@supabase/supabase-js';

export type SessionSummary = {
  id: string;
  title: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  sets_done: number;
  volume: number;
  exercises: number;
  duration_s: number;
  pb_count: number;
};
export type SetRow = {
  id: string;
  exercise_key: string;
  exercise_name: string;
  exercise_index: number;
  set_index: number;
  planned_reps: number | null;
  planned_weight: number | null;
  actual_reps: number | null;
  actual_weight: number | null;
};
export type PbRow = { exercise_id: string; exercise_key: string; exercise_name: string; kind: 'max_weight' | 'e1rm' | 'max_reps'; value: number; weight: number | null; reps: number | null; achieved_at: string };

export const PB_LABEL: Record<PbRow['kind'], string> = { max_weight: 'Heaviest', e1rm: 'Est. 1RM', max_reps: 'Most reps' };

export async function listHistory(c: SupabaseClient, before?: string, limit = 20): Promise<SessionSummary[]> {
  let q = c.from('session_summaries').select('*').eq('status', 'finished').order('started_at', { ascending: false }).limit(limit);
  if (before) q = q.lt('started_at', before);
  const { data, error } = await q;
  if (error) throw error;
  return data as SessionSummary[];
}

export async function getSessionDetail(c: SupabaseClient, id: string) {
  const [summary, sets, pbs] = await Promise.all([
    c.from('session_summaries').select('*').eq('id', id).single(),
    c.from('session_sets').select('*').eq('session_id', id).order('exercise_index').order('set_index'),
    c.from('personal_bests').select('exercise_key,exercise_name,kind,value,weight,reps,achieved_at,previous_value').eq('session_id', id).not('previous_value', 'is', null),
  ]);
  if (summary.error) throw summary.error;
  return { summary: summary.data as SessionSummary, sets: (sets.data ?? []) as SetRow[], pbs: (pbs.data ?? []) as Array<PbRow & { previous_value: number }> };
}

/** Groups sets by exercise in session order. */
export function groupSets(sets: SetRow[]): Array<{ key: string; name: string; sets: SetRow[] }> {
  const out: Array<{ key: string; name: string; sets: SetRow[] }> = [];
  const byKey = new Map<string, { key: string; name: string; sets: SetRow[] }>();
  for (const s of [...sets].sort((a, b) => a.exercise_index - b.exercise_index || a.set_index - b.set_index)) {
    let g = byKey.get(s.exercise_key);
    if (!g) {
      g = { key: s.exercise_key, name: s.exercise_name, sets: [] };
      byKey.set(s.exercise_key, g);
      out.push(g);
    }
    g.sets.push(s);
  }
  return out;
}

/** "planned 5×100 → did 4×102.5" style diff; null when they match or nothing was planned. */
export function plannedVsActual(s: SetRow): string | null {
  if (s.planned_reps == null && s.planned_weight == null) return null;
  if (s.planned_reps === s.actual_reps && Number(s.planned_weight) === Number(s.actual_weight)) return null;
  return `planned ${s.planned_reps ?? '–'}×${s.planned_weight ?? '–'}`;
}

export async function listPersonalBests(c: SupabaseClient): Promise<PbRow[]> {
  const { data, error } = await c.from('current_personal_bests').select('*').order('exercise_name');
  if (error) throw error;
  return data as PbRow[];
}

export function fmtDuration(s: number): string {
  const m = Math.round(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
}
